import { Fragment, memo, useCallback, useEffect } from "react"
import { lowerFirst } from "lodash"
import { DeliveryOrder, Message, StopUpdate } from "@/reducers/messagesSlice"
import { useAppDispatch } from "@/reducers/hooks"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import GalleryLink from "./SlickAIGalleryLink"
import { updatePDFViewer, updateImageGallery } from "@/reducers/overlaysSlice"
import { isMobile } from "@/utils/detectDevice"
import { mobileRoutes, setMobileRoute } from "@/routes/routes"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"

type LinkType = "cmr" | "deliveryOrder"

type LinkedFile = {
  url: string
  name?: string
  message?: Message
  /** A GPS link: short "type · date" label, listed inline. */
  gps?: boolean
}

type SlickAITouchedBadgeProps = {
  routeUpdates: null | StopUpdate[]
  hasRouteUnchanged: boolean
  translation: boolean
  transcription: boolean
  deliveryOrder?: DeliveryOrder
  message: Message
  linkedFiles: LinkedFile[]
  linkType?: LinkType
}

const buildRoutesMessagesOfResult = (
  stopUpdates: StopUpdate[],
  type: "added" | "updated",
  t: TFunction,
): string[] => {
  const parts: string[] = []
  const touched = stopUpdates.filter((update) => update.result === type)
  const approximate = touched.filter((update) => update.is_approximate)
  const touchedL = touched.length
  const approximateL = approximate.length

  if (touchedL === 1 && approximateL === 1) {
    parts.push(
      type === "added"
        ? t("chat.slickAIBadge.route.addedApproximate")
        : t("chat.slickAIBadge.route.updatedApproximate"),
    )
  } else if (touchedL >= 1) {
    parts.push(
      type === "added"
        ? t("chat.slickAIBadge.route.added", { count: touchedL })
        : t("chat.slickAIBadge.route.updated", { count: touchedL }),
    )
  }

  return parts
}

export const buildRoutesMessage = (
  routeUpdates: StopUpdate[],
  t: TFunction,
) => {
  if (routeUpdates.length === 0) {
    return t("chat.slickAIBadge.route.newStop")
  }

  const parts: string[] = []
  for (const part of buildRoutesMessagesOfResult(routeUpdates, "added", t)) {
    parts.push(part)
  }
  for (const part of buildRoutesMessagesOfResult(routeUpdates, "updated", t)) {
    parts.push(part)
  }
  const notAdded = routeUpdates.filter(
    (update) => update.result !== "added" && update.result !== "updated",
  )
  const notAddedL = notAdded.length

  if (notAddedL > 0) {
    parts.push(t("chat.slickAIBadge.route.notLocated", { count: notAddedL }))
  }

  if (parts[0]) {
    parts[0] = lowerFirst(parts[0])
  }

  return parts.join(" ")
}

const LINK_LABEL_KEYS = {
  cmr: "chat.slickAIBadge.foundCmr",
  deliveryOrder: "chat.slickAIBadge.foundOrder",
} as const

// One attachment on a line of its own, so a long name shares one left edge
// instead of starting mid-sentence; min-w-0/overflow-wrap are pinned here
// (not inherited) so a phone-width column still shrinks it.
const AttachmentLine = ({ children }: { children: React.ReactNode }) => (
  <span className="block min-w-0 [overflow-wrap:anywhere]">{children}</span>
)

const LinkedFileLink = ({
  linkedFileUrl,
  linkedFileName,
  linkedMessage,
  message,
}: {
  linkedFileUrl: string
  linkedFileName?: string
  linkedMessage?: Message
  message: Message
}) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const mappedUrl = mapFileUrlToSameOrigin(linkedFileUrl)
  const isPdf = /\.pdf$/i.test(linkedFileUrl)
  const isImage = /\.gif|\.png|\.jpg|\.jpeg/i.test(linkedFileUrl)
  const label = linkedFileName || t("chat.slickAIBadge.fileFallback")

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const viewerMessage = linkedMessage || message
      if (isPdf) {
        dispatch(
          updatePDFViewer({
            visible: true,
            PDFMessage: { ...viewerMessage, url: mappedUrl },
          }),
        )
      } else if (isImage) {
        dispatch(
          updateImageGallery({
            visible: true,
            directImage: { src: mappedUrl, message: linkedMessage },
          }),
        )
        if (isMobile) {
          setMobileRoute(mobileRoutes.GALLERY)
        }
      }
    },
    [dispatch, isPdf, isImage, mappedUrl, message, linkedMessage],
  )

  if (isPdf || isImage) {
    return (
      <a href={mappedUrl} onClick={handleClick}>
        <span className="text-blue-600 underline hover:text-blue-800">
          {label}
        </span>
      </a>
    )
  }

  return (
    <a href={mappedUrl} target="_blank" rel="noopener noreferrer">
      <span className="text-blue-600 underline hover:text-blue-800">
        {label}
      </span>
    </a>
  )
}

const SlickAITouchedBadge = ({
  routeUpdates,
  translation,
  transcription,
  hasRouteUnchanged,
  deliveryOrder,
  message,
  linkedFiles,
  linkType,
}: SlickAITouchedBadgeProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const openPDFViwer = useCallback(() => {
    if (deliveryOrder?.fileUrl) {
      dispatch(
        updatePDFViewer({
          visible: true,
          PDFMessage: {
            ...message,
            url: deliveryOrder?.fileUrl,
          },
        }),
      )
    }
  }, [deliveryOrder?.fileUrl, dispatch, message])

  let textMessage: string | JSX.Element | undefined
  if (routeUpdates) {
    textMessage = buildRoutesMessage(routeUpdates, t)
  } else if (translation && transcription) {
    textMessage = t("chat.slickAIBadge.translatedAndTranscribed")
  } else if (translation) {
    textMessage = t("chat.slickAIBadge.translated")
  } else if (transcription) {
    textMessage = t("chat.slickAIBadge.transcribed")
  } else if (hasRouteUnchanged) {
    textMessage = t("chat.slickAIBadge.routeUnchanged")
  } else if (
    deliveryOrder &&
    deliveryOrder.files &&
    deliveryOrder.files.length > 0
  ) {
    textMessage = (
      <span>
        {t("chat.slickAIBadge.createdFromOrder")}
        {deliveryOrder.files.map((file, index) => (
          <AttachmentLine key={index}>
            {/\.pdf$/i.test(file.fileUrl ?? "") ? (
              <a
                href={file.fileUrl}
                onClick={(e) => {
                  e.preventDefault()
                  dispatch(
                    updatePDFViewer({
                      visible: true,
                      PDFMessage: {
                        ...message,
                        url: file.fileUrl,
                      },
                    }),
                  )
                }}
              >
                <span className="text-blue-600 underline hover:text-blue-800">
                  {file.fileName || t("chat.slickAIBadge.fileFallback")}
                </span>
              </a>
            ) : (
              <GalleryLink href={file.fileUrl!} messageId={message.id}>
                <span className="text-blue-600 underline hover:text-blue-800">
                  {file.fileName || t("chat.slickAIBadge.fileFallback")}
                </span>
              </GalleryLink>
            )}
          </AttachmentLine>
        ))}
      </span>
    )
  } else if (deliveryOrder && !/\.pdf$/i.test(deliveryOrder.fileUrl ?? "")) {
    textMessage = (
      <span>
        {t("chat.slickAIBadge.createdFromOrderImage")}
        <AttachmentLine>
          <GalleryLink href={deliveryOrder.fileUrl!} messageId={message.id}>
            <span className="text-blue-600 underline hover:text-blue-800">
              {deliveryOrder.fileName || t("chat.slickAIBadge.fileFallback")}
            </span>
          </GalleryLink>
        </AttachmentLine>
      </span>
    )
  } else if (
    deliveryOrder &&
    deliveryOrder.fileUrl &&
    /\.pdf$/i.test(deliveryOrder.fileUrl ?? "")
  ) {
    textMessage = (
      <span>
        {t("chat.slickAIBadge.createdFromOrder")}
        <AttachmentLine>
          <a
            href={deliveryOrder.fileUrl}
            onClick={(e) => {
              e.preventDefault()
              openPDFViwer()
            }}
          >
            <span className="text-blue-600 underline hover:text-blue-800">
              {deliveryOrder.fileName || t("chat.slickAIBadge.fileFallback")}
            </span>
          </a>
        </AttachmentLine>
      </span>
    )
  }

  const linkedFileBadges =
    linkedFiles.length > 0 && linkType ? (
      <span>
        {/* Only start a line here if a delivery-order sentence precedes it —
            otherwise this label would drop to line two, alone under "Slick AI". */}
        {textMessage && <br />}
        {t(LINK_LABEL_KEYS[linkType])}
        {linkedFiles.every((file) => file.gps) ? (
          // Short GPS labels read better as one comma-separated line.
          <span className="[overflow-wrap:anywhere]">
            {" "}
            {linkedFiles.map((file, index) => (
              <Fragment key={file.url}>
                {index > 0 && ", "}
                <LinkedFileLink
                  linkedFileUrl={file.url}
                  linkedFileName={file.name}
                  linkedMessage={file.message}
                  message={message}
                />
              </Fragment>
            ))}
          </span>
        ) : (
          linkedFiles.map((file) => (
            <AttachmentLine key={file.url}>
              <LinkedFileLink
                linkedFileUrl={file.url}
                linkedFileName={file.name}
                linkedMessage={file.message}
                message={message}
              />
            </AttachmentLine>
          ))
        )}
      </span>
    ) : null

  useEffect(() => {
    window.document.dispatchEvent(new CustomEvent("suggest-tracking"))
  }, [])

  return (
    <span>
      {textMessage}
      {linkedFileBadges}
    </span>
  )
}

export default memo(SlickAITouchedBadge)
